import React, { useState, useEffect, useCallback } from 'react';
import { TimeInput } from '@/components/ui/date-input';
import { motion } from 'framer-motion';
import {
  Settings,
  MapPin,
  Clock,
  Save,
  Loader2,
  Award,
  Plus,
  Trash2,
  Gift,
  Bell,
  BellOff,
  BellRing,
  Calendar,
  DollarSign,
  Trophy,
  AlertCircle,
  Building,
  Timer
} from 'lucide-react';
import LocationsManagement from '@/components/hr/LocationsManagement';
import ShiftsManagement from '@/components/hr/ShiftsManagement';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import notificationService from '@/services/notificationService';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HRSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Attendance Config
  const [config, setConfig] = useState({
    office_latitude: 25.2048,
    office_longitude: 55.2708,
    allowed_radius_meters: 100,
    timezone_offset_hours: 4,
    timezone_name: 'Asia/Dubai',
    shift_start_time: '10:00',
    shift_end_time: '19:00',
    grace_period_minutes: 10,
    late_threshold_minutes: 60,
    half_day_threshold_minutes: 120,
    working_days: [0, 1, 2, 3, 4, 5],
    auto_absent_time: '14:00'
  });
  
  // Streak Rewards
  const [rewards, setRewards] = useState([]);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    streak_days: '',
    reward_type: 'cash_bonus',
    reward_amount: '',
    description: ''
  });

  // Notification Preferences
  const [notifPrefs, setNotifPrefs] = useState({
    shift_reminders: true,
    late_alerts: true,
    leave_updates: true,
    streak_milestones: true,
    salary_slips: true,
    admin_broadcasts: true,
    reminder_minutes_before: 30,
    browser_notifications_enabled: false
  });
  const [browserPermission, setBrowserPermission] = useState(
    typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
      ? window.Notification.permission
      : 'default'
  );
  const [notifSupported, setNotifSupported] = useState(true);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, rewardsRes, notifPrefsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/config`, { headers }),
        axios.get(`${API_BASE_URL}/hr/streak-rewards`, { headers }),
        axios.get(`${API_BASE_URL}/hr/notifications/preferences?user_id=${user?.id}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setConfig(prev => ({ ...prev, ...(configRes.data || {}) }));
      setRewards(rewardsRes.data.rewards || []);
      
      if (notifPrefsRes.data) {
        setNotifPrefs(notifPrefsRes.data);
      }
      
      // Check notification support
      setNotifSupported(notificationService.isSupported());
      setBrowserPermission(
        typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
          ? window.Notification.permission
          : 'default'
      );
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/config`,
        { ...config, updated_by: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkingDay = (dayIndex) => {
    const currentDays = config.working_days || [0, 1, 2, 3, 4, 5];
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter(d => d !== dayIndex)
      : [...currentDays, dayIndex].sort();
    setConfig({ ...config, working_days: newDays });
  };

  const handleAddReward = async () => {
    if (!rewardForm.streak_days || !rewardForm.reward_amount || !rewardForm.description) {
      toast.error('Please fill all fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/streak-rewards?creator_id=${user.id}`,
        {
          streak_days: parseInt(rewardForm.streak_days),
          reward_type: rewardForm.reward_type,
          reward_amount: parseFloat(rewardForm.reward_amount),
          description: rewardForm.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Streak reward added');
      setRewardDialogOpen(false);
      setRewardForm({ streak_days: '', reward_type: 'cash_bonus', reward_amount: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding reward:', error);
      toast.error('Failed to add reward');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!confirm('Delete this reward?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/hr/streak-rewards/${rewardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reward deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast.error('Failed to delete reward');
    }
  };

  const handleSaveNotificationPrefs = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/notifications/preferences?user_id=${user.id}`,
        notifPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableBrowserNotifications = async () => {
    try {
      const granted = await notificationService.requestPermission();
      setBrowserPermission(granted ? 'granted' : 'denied');
      
      if (granted) {
        await notificationService.registerServiceWorker();
        
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_BASE_URL}/hr/notifications/subscribe-browser?user_id=${user.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setNotifPrefs(prev => ({ ...prev, browser_notifications_enabled: true }));
        toast.success('Browser notifications enabled!');
        
        // Show a test notification
        await notificationService.showNotification('Notifications Enabled', {
          body: 'You will now receive HR notifications in your browser.',
          tag: 'test-notification'
        });
      } else {
        toast.error('Notification permission was denied');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const handleDisableBrowserNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/notifications/unsubscribe-browser?user_id=${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifPrefs(prev => ({ ...prev, browser_notifications_enabled: false }));
      toast.success('Browser notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Failed to disable notifications');
    }
  };

  const testNotification = async (type) => {
    if (browserPermission !== 'granted') {
      toast.error('Please enable browser notifications first');
      return;
    }
    
    switch (type) {
      case 'shift':
        await notificationService.showShiftReminder(config.shift_start_time);
        break;
      case 'late':
        await notificationService.showLateAttendanceAlert();
        break;
      case 'leave':
        await notificationService.showLeaveStatusNotification('approved', 'Annual');
        break;
      case 'streak':
        await notificationService.showStreakMilestoneNotification(30, 'You earned a 500 AED bonus!');
        break;
      case 'salary':
        await notificationService.showSalarySlipNotification('December 2024');
        break;
      default:
        break;
    }
    toast.success('Test notification sent!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">HR Settings</h1>
          <p className="text-wt-text-muted">Configure attendance rules and rewards</p>
        </div>
        <Button
          onClick={handleSaveConfig}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          disabled={saving}
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Clock className="h-4 w-4 mr-2" />
            Attendance Rules
          </TabsTrigger>
          <TabsTrigger value="locations" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Building className="h-4 w-4 mr-2" />
            Locations
          </TabsTrigger>
          <TabsTrigger value="shifts" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Timer className="h-4 w-4 mr-2" />
            Shifts
          </TabsTrigger>
          <TabsTrigger value="rewards" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Award className="h-4 w-4 mr-2" />
            Streak Rewards
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Attendance Rules Tab */}
        <TabsContent value="attendance" className="mt-4 space-y-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Timezone Settings</CardTitle>
              <CardDescription>Configure your local timezone for attendance calculations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={config.timezone_name}
                    onValueChange={(value) => {
                      const timezones = {
                        'Asia/Dubai': { offset: 4, name: 'Asia/Dubai' },
                        'Asia/Kolkata': { offset: 5.5, name: 'Asia/Kolkata' },
                        'Asia/Riyadh': { offset: 3, name: 'Asia/Riyadh' },
                        'Asia/Kuwait': { offset: 3, name: 'Asia/Kuwait' },
                        'Asia/Bahrain': { offset: 3, name: 'Asia/Bahrain' },
                        'Asia/Qatar': { offset: 3, name: 'Asia/Qatar' },
                        'Europe/London': { offset: 0, name: 'Europe/London' },
                        'America/New_York': { offset: -5, name: 'America/New_York' },
                        'Asia/Singapore': { offset: 8, name: 'Asia/Singapore' },
                        'Australia/Sydney': { offset: 11, name: 'Australia/Sydney' }
                      };
                      const tz = timezones[value];
                      setConfig({...config, timezone_name: tz.name, timezone_offset_hours: tz.offset});
                    }}
                  >
                    <SelectTrigger className="bg-wt-bg border-wt-border">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Dubai">Dubai (UTC+4)</SelectItem>
                      <SelectItem value="Asia/Riyadh">Saudi Arabia (UTC+3)</SelectItem>
                      <SelectItem value="Asia/Kuwait">Kuwait (UTC+3)</SelectItem>
                      <SelectItem value="Asia/Bahrain">Bahrain (UTC+3)</SelectItem>
                      <SelectItem value="Asia/Qatar">Qatar (UTC+3)</SelectItem>
                      <SelectItem value="Asia/Kolkata">India (UTC+5:30)</SelectItem>
                      <SelectItem value="Asia/Singapore">Singapore (UTC+8)</SelectItem>
                      <SelectItem value="Europe/London">London (UTC+0)</SelectItem>
                      <SelectItem value="America/New_York">New York (UTC-5)</SelectItem>
                      <SelectItem value="Australia/Sydney">Sydney (UTC+11)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-wt-text-muted">Used for attendance time calculations</p>
                </div>
                <div className="space-y-2">
                  <Label>Current UTC Offset</Label>
                  <div className="h-10 px-3 py-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary flex items-center">
                    UTC{(config.timezone_offset_hours || 4) >= 0 ? '+' : ''}{config.timezone_offset_hours || 4}
                  </div>
                  <p className="text-xs text-wt-text-muted">Hours ahead/behind UTC</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Shift Timing</CardTitle>
              <CardDescription>Configure daily work schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Shift Start Time</Label>
                  <TimeInput
                    value={config.shift_start_time}
                    onChange={(e) => setConfig({...config, shift_start_time: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shift End Time</Label>
                  <TimeInput
                    value={config.shift_end_time}
                    onChange={(e) => setConfig({...config, shift_end_time: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Attendance Classification</CardTitle>
              <CardDescription>Rules for classifying attendance status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Grace Period (minutes)</Label>
                  <Input
                    type="number"
                    value={config.grace_period_minutes}
                    onChange={(e) => setConfig({...config, grace_period_minutes: parseInt(e.target.value)})}
                    className="bg-wt-bg border-wt-border"
                  />
                  <p className="text-xs text-wt-text-muted">On-time if within this period</p>
                </div>
                <div className="space-y-2">
                  <Label>Late Threshold (minutes)</Label>
                  <Input
                    type="number"
                    value={config.late_threshold_minutes}
                    onChange={(e) => setConfig({...config, late_threshold_minutes: parseInt(e.target.value)})}
                    className="bg-wt-bg border-wt-border"
                  />
                  <p className="text-xs text-wt-text-muted">Marked late after grace period</p>
                </div>
                <div className="space-y-2">
                  <Label>Half-Day Threshold (minutes)</Label>
                  <Input
                    type="number"
                    value={config.half_day_threshold_minutes}
                    onChange={(e) => setConfig({...config, half_day_threshold_minutes: parseInt(e.target.value)})}
                    className="bg-wt-bg border-wt-border"
                  />
                  <p className="text-xs text-wt-text-muted">Half-day if beyond this</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Auto-Absent Time</Label>
                <TimeInput
                  value={config.auto_absent_time}
                  onChange={(e) => setConfig({...config, auto_absent_time: e.target.value})}
                  className="bg-wt-bg border-wt-border w-48"
                />
                <p className="text-xs text-wt-text-muted">Mark absent if no check-in by this time</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Working Days</CardTitle>
              <CardDescription>Select days when attendance is required</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                {dayNames.map((day, index) => (
                  <Button
                    key={day}
                    variant={(config.working_days || []).includes(index) ? 'default' : 'outline'}
                    className={
                      (config.working_days || []).includes(index)
                        ? 'bg-wt-gold text-black hover:bg-wt-gold-hover'
                        : 'border-wt-border text-wt-text-muted hover:text-wt-gold'
                    }
                    onClick={() => toggleWorkingDay(index)}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary">Attendance Logic Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="text-wt-text-primary">On Time:</span>
                  <span className="text-wt-text-muted">
                    {config.shift_start_time || '10:00'} - {(() => {
                      const startTime = config.shift_start_time || '10:00';
                      const [h, m] = startTime.split(':').map(Number);
                      const grace = config.grace_period_minutes || 10;
                      const newM = m + grace;
                      return `${String(h + Math.floor(newM / 60)).padStart(2, '0')}:${String(newM % 60).padStart(2, '0')}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                  <span className="text-wt-text-primary">Late:</span>
                  <span className="text-wt-text-muted">
                    After grace period up to {config.half_day_threshold_minutes || 120} minutes
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                  <span className="text-wt-text-primary">Half Day:</span>
                  <span className="text-wt-text-muted">
                    After {config.half_day_threshold_minutes} minutes from shift start
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-wt-text-primary">Absent:</span>
                  <span className="text-wt-text-muted">
                    No check-in by {config.auto_absent_time}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-4">
          <LocationsManagement />
        </TabsContent>

        {/* Shifts Tab */}
        <TabsContent value="shifts" className="mt-4">
          <ShiftsManagement />
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards" className="mt-4 space-y-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-wt-text-primary">Attendance Streak Rewards</CardTitle>
                <CardDescription>Configure rewards for consecutive attendance</CardDescription>
              </div>
              <Button
                onClick={() => setRewardDialogOpen(true)}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Reward
              </Button>
            </CardHeader>
            <CardContent>
              {rewards.length === 0 ? (
                <div className="text-center py-8 text-wt-text-muted">
                  <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No streak rewards configured</p>
                  <p className="text-xs mt-1">Add rewards to motivate employees</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rewards.map((reward) => (
                    <motion.div
                      key={reward.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-wt-gold/20 flex items-center justify-center">
                          <span className="text-lg font-bold text-wt-gold">{reward.streak_days}</span>
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{reward.description}</p>
                          <p className="text-sm text-wt-text-muted">
                            {reward.streak_days} consecutive days
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-wt-gold">{reward.reward_amount} AED</p>
                          <p className="text-xs text-wt-text-muted capitalize">
                            {reward.reward_type.replace('_', ' ')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteReward(reward.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-4 space-y-4">
          {/* Browser Notifications Status */}
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <BellRing className="h-5 w-5 text-wt-gold" />
                Browser Notifications
              </CardTitle>
              <CardDescription>Enable real-time notifications in your browser</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!notifSupported ? (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span>Your browser does not support notifications</span>
                  </div>
                </div>
              ) : browserPermission === 'denied' ? (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 text-red-400">
                    <BellOff className="h-5 w-5" />
                    <span>Notifications are blocked. Please enable them in your browser settings.</span>
                  </div>
                </div>
              ) : browserPermission === 'granted' ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-400">
                        <Bell className="h-5 w-5" />
                        <span>Browser notifications are enabled</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisableBrowserNotifications}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <BellOff className="h-4 w-4 mr-2" />
                        Disable
                      </Button>
                    </div>
                  </div>
                  
                  {/* Test Notifications */}
                  <div className="space-y-2">
                    <Label className="text-wt-text-muted">Test Notifications</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testNotification('shift')}
                        className="border-wt-border"
                        data-testid="test-shift-notification"
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        Shift Reminder
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testNotification('late')}
                        className="border-wt-border"
                        data-testid="test-late-notification"
                      >
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Late Alert
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testNotification('leave')}
                        className="border-wt-border"
                        data-testid="test-leave-notification"
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Leave Update
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testNotification('streak')}
                        className="border-wt-border"
                        data-testid="test-streak-notification"
                      >
                        <Trophy className="h-4 w-4 mr-1" />
                        Streak Milestone
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testNotification('salary')}
                        className="border-wt-border"
                        data-testid="test-salary-notification"
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Salary Slip
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-wt-bg border border-wt-border">
                    <p className="text-wt-text-primary mb-2">Enable notifications to receive:</p>
                    <ul className="text-sm text-wt-text-muted space-y-1 ml-4 list-disc">
                      <li>Shift start reminders</li>
                      <li>Late attendance alerts</li>
                      <li>Leave request updates</li>
                      <li>Attendance streak milestones</li>
                      <li>Salary slip availability</li>
                    </ul>
                  </div>
                  <Button
                    onClick={handleEnableBrowserNotifications}
                    className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                    data-testid="enable-notifications-btn"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Enable Browser Notifications
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-wt-text-primary">Notification Preferences</CardTitle>
                <CardDescription>Choose which notifications you want to receive</CardDescription>
              </div>
              <Button
                onClick={handleSaveNotificationPrefs}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                disabled={saving}
                data-testid="save-notification-prefs"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Preferences
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Shift Reminders */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Shift Reminders</p>
                    <p className="text-sm text-wt-text-muted">Get notified before your shift starts</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.shift_reminders}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, shift_reminders: checked }))}
                  data-testid="switch-shift-reminders"
                />
              </div>

              {/* Late Alerts */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Late Attendance Alerts</p>
                    <p className="text-sm text-wt-text-muted">Alert when you haven't marked attendance</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.late_alerts}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, late_alerts: checked }))}
                  data-testid="switch-late-alerts"
                />
              </div>

              {/* Leave Updates */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Leave Updates</p>
                    <p className="text-sm text-wt-text-muted">Notifications for leave request status changes</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.leave_updates}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, leave_updates: checked }))}
                  data-testid="switch-leave-updates"
                />
              </div>

              {/* Streak Milestones */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Trophy className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Streak Milestones</p>
                    <p className="text-sm text-wt-text-muted">Celebrate when you hit attendance streaks</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.streak_milestones}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, streak_milestones: checked }))}
                  data-testid="switch-streak-milestones"
                />
              </div>

              {/* Salary Slips */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Salary Slips</p>
                    <p className="text-sm text-wt-text-muted">Notified when salary slip is available</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.salary_slips}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, salary_slips: checked }))}
                  data-testid="switch-salary-slips"
                />
              </div>

              {/* Admin Broadcasts */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <BellRing className="h-5 w-5 text-red-400" />
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">Admin Broadcasts</p>
                    <p className="text-sm text-wt-text-muted">Important announcements from HR</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs.admin_broadcasts}
                  onCheckedChange={(checked) => setNotifPrefs(prev => ({ ...prev, admin_broadcasts: checked }))}
                  data-testid="switch-admin-broadcasts"
                />
              </div>

              {/* Reminder Time Setting */}
              {notifPrefs.shift_reminders && (
                <div className="p-4 rounded-lg bg-wt-bg border border-wt-border">
                  <Label className="mb-2 block">Shift Reminder Time</Label>
                  <Select
                    value={String(notifPrefs.reminder_minutes_before)}
                    onValueChange={(v) => setNotifPrefs(prev => ({ ...prev, reminder_minutes_before: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-48 bg-wt-surface border-wt-border" data-testid="reminder-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="15">15 minutes before</SelectItem>
                      <SelectItem value="30">30 minutes before</SelectItem>
                      <SelectItem value="45">45 minutes before</SelectItem>
                      <SelectItem value="60">1 hour before</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-wt-text-muted mt-2">
                    You'll be reminded {notifPrefs.reminder_minutes_before} minutes before your shift starts at {config.shift_start_time}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Reward Dialog */}
      <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Add Streak Reward</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Streak Days</Label>
              <Input
                type="number"
                value={rewardForm.streak_days}
                onChange={(e) => setRewardForm({...rewardForm, streak_days: e.target.value})}
                placeholder="30"
                className="bg-wt-bg border-wt-border"
              />
              <p className="text-xs text-wt-text-muted">Number of consecutive days to earn reward</p>
            </div>
            <div className="space-y-2">
              <Label>Reward Type</Label>
              <Select
                value={rewardForm.reward_type}
                onValueChange={(v) => setRewardForm({...rewardForm, reward_type: v})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="cash_bonus">Cash Bonus</SelectItem>
                  <SelectItem value="incentive_points">Incentive Points</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reward Amount (AED)</Label>
              <Input
                type="number"
                value={rewardForm.reward_amount}
                onChange={(e) => setRewardForm({...rewardForm, reward_amount: e.target.value})}
                placeholder="500"
                className="bg-wt-bg border-wt-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={rewardForm.description}
                onChange={(e) => setRewardForm({...rewardForm, description: e.target.value})}
                placeholder="30-Day Perfect Attendance Bonus"
                className="bg-wt-bg border-wt-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRewardDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddReward}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Reward'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRSettingsPage;
